import { Button } from 'antd';
import styled, { keyframes, css } from 'styled-components';

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;



export const StyledChatWindow = styled.div<{ $isVisible: boolean; $expanded?: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: ${({ $expanded }) => ($expanded ? 'min(760px, 92vw)' : '420px')};
  height: ${({ $expanded }) => ($expanded ? '88vh' : '600px')};
  max-height: 92vh;
  transition: width 0.28s ease, height 0.28s ease;
  z-index: 1000;
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12), 0 3px 6px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.background.paper};
  border: 1px solid ${({ theme }) => theme.palette.borderColor};

  ${({ $isVisible }) =>
    $isVisible
      ? css`
          animation: ${slideUp} 0.25s ease-out;
          pointer-events: auto;
          opacity: 1;
        `
      : css`
          opacity: 0;
          pointer-events: none;
          transform: translateY(20px);
        `}

  [dir='rtl'] & {
    right: auto;
    left: 24px;
  }

  @media screen and (max-width: 768px) {
    width: 100vw;
    /* dvh trims the browser toolbar so the header (with close button) is never
       hidden behind Safari chrome. vh fallback for iOS < 15.4. */
    height: 88vh;
    height: 88dvh;
    max-height: 88vh;
    max-height: 88dvh;
    bottom: 0;
    right: 0;
    left: 0;
    /* Bottom-sheet look: leave a strip of the page visible above so the user
       can tap the backdrop to dismiss. */
    border-radius: 12px 12px 0 0;

    [dir='rtl'] & {
      right: 0;
      left: 0;
    }
  }
`;

// Dimmed layer behind the chat window on mobile only — tap to close.
// Hidden on desktop so the floating-panel behavior is unchanged.
export const StyledChatBackdrop = styled.div`
  display: none;

  @media screen and (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 999;
  }
`;

// Wraps header buttons that make no sense on mobile (e.g. expand/shrink when
// the window already fills the width).
export const HideOnMobile = styled.span`
  display: contents;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const StyledChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  background-color: ${({ theme }) => theme.palette.background.paper};
  flex-shrink: 0;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const StyledChatHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const StyledHeaderButton = styled(Button)`
  &&& {
    width: 28px;
    height: 28px;
    min-width: 28px;
    padding: 0;
    padding-inline: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.palette.text.secondary};
    border: none;
    background: transparent;
    box-shadow: none;
    border-radius: 6px;
  }

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: ${({ theme }) => theme.palette.background.default};
  }
`;

export const StyledMessageListWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.palette.borderColor};
    border-radius: 4px;
  }
`;

export const StyledEmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-align: center;
  padding: 20px;

  p {
    margin: 8px 0 0;
    font-size: 13px;
    line-height: 1.5;
  }
`;

export const StyledSuggestionList = styled.div<{ $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  width: 100%;
  max-width: 320px;
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? '500px' : '160px')};
  transition: max-height 0.5s ease;
`;

const suggestionFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const StyledSuggestionItem = styled.button<{ $delay?: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.palette.text.primary};
  cursor: pointer;
  transition: background 0.15s ease;
  width: 100%;
  opacity: 0;
  animation: ${suggestionFadeIn} 0.3s ease forwards;
  animation-delay: ${({ $delay }) => ($delay ?? 0) * 120}ms;

  .suggestion-index {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    background: ${({ theme }) => theme.palette.background.default};
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 11px;
    font-weight: 600;
  }

  &:hover {
    background: ${({ theme }) => theme.palette.background.default};
  }
`;

export const StyledShowMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  margin-top: 4px;
  font-size: 12px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.palette.text.secondary};
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.background.default};
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export type GroupPosition = 'single' | 'first' | 'middle' | 'last';

const groupMarginTop = (pos: GroupPosition) => {
  if (pos === 'middle' || pos === 'last') return '2px';
  return '12px';
};

export const StyledMessageRow = styled.div<{ $isUser: boolean; $groupPosition: GroupPosition }>`
  display: flex;
  flex-direction: ${({ $isUser }) => ($isUser ? 'row-reverse' : 'row')};
  margin-top: ${({ $groupPosition }) => groupMarginTop($groupPosition)};

  &:first-child {
    margin-top: 0;
  }

  [dir='rtl'] & {
    flex-direction: ${({ $isUser }) => ($isUser ? 'row' : 'row-reverse')};
  }
`;

/**
 * Border-radius for grouped bubbles:
 * - single: full rounded (18px) with small corner on avatar side
 * - first: full top, flat bottom on avatar side
 * - middle: flat top & bottom on avatar side
 * - last: flat top on avatar side, full bottom
 *
 * Format: top-left top-right bottom-right bottom-left
 * User (right-aligned): avatar side = right
 * Assistant (left-aligned): avatar side = left
 */
const getBubbleRadius = ($isUser: boolean, pos: GroupPosition) => {
  // [top-left, top-right, bottom-right, bottom-left]
  if ($isUser) {
    switch (pos) {
      case 'first':  return '18px 18px 4px 18px';
      case 'middle': return '18px 4px 4px 18px';
      case 'last':   return '18px 4px 18px 18px';
      default:       return '18px 18px 4px 18px'; // single
    }
  }
  // assistant
  switch (pos) {
    case 'first':  return '18px 18px 18px 4px';
    case 'middle': return '4px 18px 18px 4px';
    case 'last':   return '4px 18px 18px 18px';
    default:       return '18px 18px 18px 4px'; // single
  }
};

const getBubbleRadiusRtl = ($isUser: boolean, pos: GroupPosition) => {
  // Mirrored: swap left↔right
  if ($isUser) {
    switch (pos) {
      case 'first':  return '18px 18px 18px 4px';
      case 'middle': return '4px 18px 18px 4px';
      case 'last':   return '4px 18px 18px 18px';
      default:       return '18px 18px 18px 4px';
    }
  }
  switch (pos) {
    case 'first':  return '18px 18px 4px 18px';
    case 'middle': return '18px 4px 4px 18px';
    case 'last':   return '18px 4px 18px 18px';
    default:       return '18px 18px 4px 18px';
  }
};

const pennyWordFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const StyledBubble = styled.div<{ $isUser: boolean; $groupPosition: GroupPosition }>`
  max-width: 75%;
  padding: 10px 14px;
  border-radius: ${({ $isUser, $groupPosition }) => getBubbleRadius($isUser, $groupPosition)};
  font-size: 13px;
  line-height: 1.55;
  word-break: break-word;
  overflow: hidden;
  white-space: ${({ $isUser }) => ($isUser ? 'pre-wrap' : 'normal')};
  background-color: ${({ $isUser, theme }) =>
    $isUser
      ? theme.palette.primary.main
      : theme.palette.background.default};
  color: ${({ $isUser, theme }) =>
    $isUser
      ? theme.palette.primary.contrastText
      : theme.palette.text.primary};

  [dir='rtl'] & {
    border-radius: ${({ $isUser, $groupPosition }) => getBubbleRadiusRtl($isUser, $groupPosition)};
  }

  /* Markdown styles for assistant messages */
  p {
    margin: 0 0 8px;
    &:last-child {
      margin-bottom: 0;
    }
  }

  ul, ol {
    margin: 4px 0;
    padding-left: 20px;
    list-style: revert;
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  li {
    margin: 2px 0;
    display: list-item;
  }

  h1, h2, h3, h4 {
    margin: 8px 0 4px;
    font-size: 14px;
    font-weight: 600;
  }

  pre {
    background-color: rgba(0, 0, 0, 0.06);
    padding: 8px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 4px 0;
  }

  code {
    font-family: monospace;
    font-size: 12px;
    background-color: rgba(0, 0, 0, 0.06);
    padding: 1px 4px;
    border-radius: 3px;
  }

  pre code {
    background: none;
    padding: 0;
  }

  table {
    border-collapse: collapse;
    margin: 4px 0;
    font-size: 12px;
    width: max-content;
    min-width: 100%;
  }

  th, td {
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 4px 8px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
    background-color: rgba(0, 0, 0, 0.03);
  }

  strong {
    font-weight: 600;
  }

  /* ChatGPT-style per-word fade-in for streamed text */
  .penny-fade-word {
    animation: ${pennyWordFade} 0.4s ease both;
  }

  blockquote {
    border-left: 3px solid rgba(0, 0, 0, 0.15);
    margin: 4px 0;
    padding: 2px 10px;
    color: inherit;
    opacity: 0.85;
  }
`;

// One memoized markdown block — just spacing; per-word fade is applied to the
// inner .penny-fade-word spans (see StyledBubble), so newly streamed words fade.
export const StyledMarkdownBlock = styled.div`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const StyledCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 14px;
  background-color: currentColor;
  margin-left: 1px;
  vertical-align: text-bottom;
  animation: ${blink} 1s step-end infinite;
`;

export const StyledInputArea = styled.div`
  padding: 0 16px 12px;
  background-color: ${({ theme }) => theme.palette.background.paper};
  flex-shrink: 0;
`;

export const StyledInputCard = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 12px;
  background-color: ${({ theme }) => theme.palette.background.default};
  &:focus-within {
    border-color: ${({ theme }) => theme.palette.primary.main};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.palette.primary.main}26;
  }
`;

export const StyledNativeTextArea = styled.textarea`
  display: block;
  resize: none;
  font-size: 13px;
  line-height: 1.5;
  border: none;
  outline: none;
  padding: 10px 14px 4px;
  background: transparent;
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: inherit;
  overflow-y: hidden;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
    opacity: 0.6;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.palette.borderColor};
    border-radius: 4px;
  }
`;

export const StyledInputActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 8px;
`;

export const StyledSendButton = styled(Button)`
  &&& {
    height: 28px;
    width: 28px;
    min-width: 28px;
    padding: 0;
    padding-inline: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
`;

export const StyledQuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding-bottom: 8px;
`;

export const StyledQuickActionChip = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  line-height: 1.4;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  background: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.text.secondary};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
    background: ${({ theme }) => theme.palette.background.default};
  }
`;

export const StyledToolMenu = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 280px;
`;

export const StyledToolMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.palette.text.primary};
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease;
  width: 100%;

  .tool-index {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    background: ${({ theme }) => theme.palette.background.default};
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 11px;
    font-weight: 600;
  }

  &:hover {
    background: ${({ theme }) => theme.palette.background.default};
  }
`;

export const StyledToolCallBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};


`;

export const StyledChatBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`;

export const StyledSessionPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.palette.background.paper};
  z-index: 10;
  display: flex;
  flex-direction: column;
  transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(-100%)')};
  transition: transform 0.2s ease;

  [dir='rtl'] & {
    transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(100%)')};
  }
`;

export const StyledSessionPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};

  span {
    font-weight: 600;
    font-size: 15px;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

export const StyledSessionList = styled.div`
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.palette.borderColor};
    border-radius: 4px;
  }
`;

export const StyledSessionItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  cursor: pointer;
  background-color: ${({ $isActive, theme }) =>
    $isActive ? theme.palette.background.default : 'transparent'};
  border-left: 3px solid
    ${({ $isActive, theme }) =>
      $isActive ? theme.palette.primary.main : 'transparent'};

  [dir='rtl'] & {
    border-left: none;
    border-right: 3px solid
      ${({ $isActive, theme }) =>
        $isActive ? theme.palette.primary.main : 'transparent'};
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.background.default};
  }

  .session-title {
    flex: 1;
    font-size: 13px;
    color: ${({ theme }) => theme.palette.text.primary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 8px;

    [dir='rtl'] & {
      margin-right: 0;
      margin-left: 8px;
    }
  }

`;

export const StyledNewChatButton = styled(Button)`
  margin: 8px 16px;
  width: calc(100% - 32px);
`;

export const StyledExpandButton = styled(Button)`
  &&& {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 2;
    width: 24px;
    height: 24px;
    min-width: 24px;
    padding: 0;
    padding-inline: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    opacity: 0;
    transition: opacity 0.15s ease, box-shadow 0.15s ease;
  }

  &:hover {
    background: #fff !important;
    border-color: rgba(0, 0, 0, 0.25);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  }
`;

export const StyledTableWrapper = styled.div`
  position: relative;
  margin: 4px 0;

  &:hover ${StyledExpandButton} {
    opacity: 1;
  }
`;

export const StyledTableScroll = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }

  & > table {
    margin: 0;
    border: none;
  }
`;

export const FeedbackWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 12px;
  color: #8c8c8c;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.palette.borderColor};
`;

export const FeedbackCommentInput = styled.input`
  flex: 1;
  font-size: 13px;
  line-height: 1.5;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 12px;
  outline: none;
  padding: 6px 14px;
  background: ${({ theme }) => theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.primary};
  font-family: inherit;
  height: 32px;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.palette.text.secondary};
    opacity: 0.6;
  }

  &:focus {
    border-color: ${({ theme }) => theme.palette.primary.main};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.palette.primary.main}26;
  }
`;

export const StyledModalTableWrapper = styled.div`
  table {
    border-collapse: collapse;
    font-size: 13px;
  }

  th, td {
    border: 1px solid rgba(0, 0, 0, 0.15);
    padding: 6px 10px;
    text-align: left;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
    background-color: rgba(0, 0, 0, 0.04);
  }

  tr:hover td {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;
